import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { saveQueueStats, resetQueueStats } from "../../features/Queue/QueueSlice";

const extractQueueItems = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getQueueItemStatus = (item) => {
  const status = item?.sStatus ?? item?.status ?? item?.queueStatus ?? "";
  return String(status).toLowerCase();
};

export default function useQueueCount() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const sharedProcessingCount = useSelector((state) => state.queue?.stats?.processing ?? 0);

  const refreshQueueCount = useCallback(async () => {
    if (!token) {
      dispatch(resetQueueStats());
      return 0;
    }

    try {
      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}`,
        method: "GET",
        authToken: { token },
        noCache: true,
      });

      const items = extractQueueItems(response);
      const nextStats = Array.isArray(items)
        ? items.reduce(
            (stats, item) => {
              const status = getQueueItemStatus(item);
              stats.total += 1;
              if (Object.prototype.hasOwnProperty.call(stats, status)) {
                stats[status] += 1;
              }
              return stats;
            },
            { total: 0, processing: 0, queued: 0, completed: 0, failed: 0 },
          )
        : { total: 0, processing: 0, queued: 0, completed: 0, failed: 0 };
      const nextCount = nextStats.processing;
      dispatch(saveQueueStats(nextStats));
      return nextCount;
    } catch (error) {
      console.error("Failed to fetch queue count:", error);
      return 0;
    }
  }, [token, dispatch]);

  useEffect(() => {
    refreshQueueCount();
  }, [refreshQueueCount]);

  useEffect(() => {
    if (!token) return undefined;

    const intervalId = setInterval(() => {
      refreshQueueCount();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [token, refreshQueueCount]);

  return sharedProcessingCount;
}
